import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRScanner } from './components/QRScanner';
import { CameraPreview } from './components/CameraPreview';
import { PhotoReview } from './components/PhotoReview';
import { addPhoto } from '../../lib/db/photos';
import { getCaptureConfig } from '../../lib/config/capture';
import { addToSyncQueue } from '../../lib/db/sync-queue';
import { DEFAULT_CAPTURE_CONFIG } from '@photobooth/types';
import { LogoutModal } from '../../components/LogoutModal';
import { useOutletAuth } from '../../lib/auth/OutletAuthProvider';
import {
  savePhotoToBridge,
  sendFolderToPrintServer,
  getStorageFolderName,
} from '../../lib/bridge/storage-client';
import type { LocalPhoto, LocalProduct } from '../../lib/db/schema';
import type { CaptureConfig, Transaction, Product } from '@photobooth/types';
import { createPhotoBoothAPI } from '@photobooth/api-client';
import { getEffectiveApiBaseUrl } from '../../lib/config/terminal-urls';

type CaptureState = 'scanning' | 'validating' | 'capturing' | 'reviewing' | 'completed' | 'error';

interface CaptureTransaction {
  transactionId: string;
  customerName: string;
  productId: string;
}

interface CaptureSession {
  transaction: CaptureTransaction;
  product: LocalProduct;
  capturedPhotos: number;
  totalShots: number;
  /** Object URLs for thumbnails of photos already captured this session; revoke on session end */
  capturedPreviewUrls: string[];
}

/** Resolve effective capture config: product overrides when > 0, else global */
function resolveCaptureConfig(product: LocalProduct, globalConfig: CaptureConfig): Pick<CaptureConfig, 'countdownSeconds' | 'reviewPhotoSeconds'> {
  return {
    countdownSeconds: (product.countdownSeconds ?? 0) > 0 ? product.countdownSeconds! : globalConfig.countdownSeconds,
    reviewPhotoSeconds: (product.reviewPhotoSeconds ?? 0) > 0 ? product.reviewPhotoSeconds! : globalConfig.reviewPhotoSeconds,
  };
}

const apiClient = createPhotoBoothAPI({
  baseUrl: getEffectiveApiBaseUrl(),
});

export default function CaptureTerminal() {
  const navigate = useNavigate();
  const { outlet, authState, clearAuthentication } = useOutletAuth();
  const [state, setState] = useState<CaptureState>('scanning');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [session, setSession] = useState<CaptureSession | null>(null);
  const [lastCapturedBlob, setLastCapturedBlob] = useState<Blob | null>(null);
  const [manualOrderId, setManualOrderId] = useState('');
  const [captureConfig, setCaptureConfig] = useState(DEFAULT_CAPTURE_CONFIG);
  const [error, setError] = useState<string | null>(null);
  const [storageError, setStorageError] = useState<string | null>(null);
  const [storageErrorPreviewUrl, setStorageErrorPreviewUrl] = useState<string | null>(null);
  const [countdownValue, setCountdownValue] = useState<number | null>(null);
  const [reviewCountdown, setReviewCountdown] = useState<number | null>(null);
  const [returnCountdown, setReturnCountdown] = useState<number | null>(null);
  const [errorReturnCountdown, setErrorReturnCountdown] = useState<number | null>(null);

  useEffect(() => {
    if ((state === 'capturing' || state === 'reviewing') && session) {
      getCaptureConfig().then(setCaptureConfig);
    }
  }, [state, session]);

  useEffect(() => {
    if (state !== 'reviewing') setReviewCountdown(null);
  }, [state]);

  const prevStateRef = useRef<CaptureState>(state);
  useEffect(() => {
    if (state === 'capturing' && prevStateRef.current === 'reviewing') {
      setLastCapturedBlob(null);
    }
    prevStateRef.current = state;
  }, [state]);

  useEffect(() => {
    if (state !== 'completed') {
      setReturnCountdown(null);
      return;
    }
    setReturnCountdown(5);
    const interval = setInterval(() => {
      setReturnCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          handleStartNewRef.current();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [state]);

  useEffect(() => {
    if (state !== 'error') {
      setErrorReturnCountdown(null);
      return;
    }
    setErrorReturnCountdown(5);
    const interval = setInterval(() => {
      setErrorReturnCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          setState('scanning');
          setError(null);
          setManualOrderId('');
          return null;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [state]);

  const handleQRScan = useCallback(async (qrData: string) => {
    setState('validating');
    setError(null);

    try {
      // Extract transaction ID from QR data
      const transactionId = qrData.trim();

      // Validate transaction from API by ID (no IndexedDB fallback)
      const response = await apiClient.transactions.getById(transactionId);

      if (!response.success || !response.data) {
        throw new Error('Transaction not found. Please ensure payment was completed.');
      }

      const transaction = response.data as Transaction;

      // Validate payment status from API
      if (transaction.payment.status !== 'completed') {
        throw new Error('Payment not completed. Please complete payment first.');
      }

      if (transaction.captureStatus === 'completed') {
        throw new Error('Photos already captured for this transaction.');
      }

      if (transaction.captureStatus !== 'pending') {
        throw new Error('Capture already in progress or not available for this transaction.');
      }

      // Get product details from API
      const productResponse = await apiClient.products.getById(transaction.productId);
      if (!productResponse.success || !productResponse.data) {
        throw new Error('Product not found. Please try again.');
      }
      const apiProduct = productResponse.data as Product;
      const product: LocalProduct = {
        productId: apiProduct.productId,
        categoryId: apiProduct.categoryId,
        name: apiProduct.name,
        description: apiProduct.description ?? '',
        shotCount: apiProduct.shotCount,
        printCount: apiProduct.printCount,
        paperType: apiProduct.paperType as 'collage_4x6' | 'strip',
        basePrice: apiProduct.basePrice,
        price: apiProduct.basePrice,
        originalPrice: apiProduct.basePrice,
        hasOutletPricing: false,
        isActive: apiProduct.isActive,
        syncedAt: Date.now(),
        countdownSeconds: apiProduct.countdownSeconds,
        reviewPhotoSeconds: apiProduct.reviewPhotoSeconds,
        editCountdownSeconds: apiProduct.editCountdownSeconds,
      };

      const shotCount = product.shotCount;

      // Start capture session (include product for capture config overrides)
      setSession({
        transaction: {
          transactionId: transaction.transactionId,
          customerName: transaction.customer.name,
          productId: transaction.productId,
        },
        product,
        capturedPhotos: 0,
        totalShots: shotCount,
        capturedPreviewUrls: [],
      });
      
      setState('capturing');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to validate transaction';
      setError(errorMsg);
      setState('error');
    }
  }, []);

  const handleCaptureStartClick = useCallback(async () => {
    if (!session) return;
    const transactionId = session.transaction.transactionId;
    try {
      await apiClient.transactions.updateCaptureStatus(transactionId, 'in_progress');
    } catch (apiErr) {
      console.warn('Failed to update capture status on API:', apiErr);
    }
  }, [session?.transaction.transactionId]);

  const handlePhotoCapture = (imageBlob: Blob) => {
    if (!session) return;
    setLastCapturedBlob(imageBlob);
    setState('reviewing');
  };

  const handleReviewComplete = async () => {
    if (!session || !lastCapturedBlob) return;

    try {
      // Save to Bridge storage first; on failure show error and allow Retake
      try {
        await savePhotoToBridge(
          lastCapturedBlob,
          session.transaction.transactionId,
          session.transaction.customerName,
          session.capturedPhotos + 1
        );
      } catch (bridgeErr) {
        const msg = bridgeErr instanceof Error ? bridgeErr.message : 'Failed to save photo to storage';
        setStorageErrorPreviewUrl(URL.createObjectURL(lastCapturedBlob));
        setStorageError(msg);
        return;
      }

      setStorageError(null);
      setStorageErrorPreviewUrl(null);

      const photoId = `photo_${session.transaction.transactionId}_${Date.now()}`;
      const photoData: LocalPhoto = {
        photoId,
        transactionId: session.transaction.transactionId,
        outletId: authState?.outletId,
        imageBlob: lastCapturedBlob,
        sequence: session.capturedPhotos + 1,
        capturedAt: Date.now(),
        uploaded: false,
      };

      await addPhoto(photoData);
      await addToSyncQueue({
        entityType: 'photo',
        entityId: photoId,
        operation: 'create',
        payload: JSON.stringify({
          photoId: photoData.photoId,
          transactionId: photoData.transactionId,
          outletId: photoData.outletId,
          sequence: photoData.sequence,
          capturedAt: photoData.capturedAt,
        }),
        createdAt: Date.now(),
        retryCount: 0,
      });

      const newCapturedCount = session.capturedPhotos + 1;
      const previewUrl = URL.createObjectURL(lastCapturedBlob);
      setSession({
        ...session,
        capturedPhotos: newCapturedCount,
        capturedPreviewUrls: [...session.capturedPreviewUrls, previewUrl],
      });

      if (newCapturedCount >= session.totalShots) {
        const transactionId = session.transaction.transactionId;
        try {
          await apiClient.transactions.updateCaptureStatus(transactionId, 'completed');
        } catch (apiErr) {
          console.warn('Failed to update capture status on API:', apiErr);
        }
        [...session.capturedPreviewUrls, previewUrl].forEach(URL.revokeObjectURL);
        setSession(prev => prev ? { ...prev, capturedPreviewUrls: [] } : null);
        setState('completed');

        try {
          const folderName = getStorageFolderName(
            session.transaction.transactionId,
            session.transaction.customerName
          );
          await sendFolderToPrintServer(folderName);
        } catch (transferErr) {
          console.warn('Transfer to Print Server failed (photos saved locally):', transferErr);
        }
      } else {
        setState('capturing');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to save photo';
      setError(errorMsg);
      setState('error');
    }
  };

  const handleStartNew = () => {
    if (storageErrorPreviewUrl) {
      URL.revokeObjectURL(storageErrorPreviewUrl);
    }
    if (session?.capturedPreviewUrls) {
      session.capturedPreviewUrls.forEach(URL.revokeObjectURL);
    }
    setSession(null);
    setError(null);
    setStorageError(null);
    setStorageErrorPreviewUrl(null);
    setLastCapturedBlob(null);
    setManualOrderId('');
    setState('scanning');
  };

  const handleStartNewRef = useRef(handleStartNew);
  handleStartNewRef.current = handleStartNew;

  const handleRetakeAfterStorageError = () => {
    if (storageErrorPreviewUrl) {
      URL.revokeObjectURL(storageErrorPreviewUrl);
    }
    setStorageErrorPreviewUrl(null);
    setStorageError(null);
    setLastCapturedBlob(null);
    setState('capturing');
  };

  const isSessionActive = state === 'capturing' || state === 'reviewing';

  return (
    <div className="min-h-screen p-4 md:p-6 bg-gradient-to-br from-cyan-50 to-blue-50">
      <div className={isSessionActive ? 'w-full max-w-full' : 'max-w-6xl mx-auto'}>
        <div className="relative z-50 flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Capture
            </h1>
            {outlet && (
              <p className="text-gray-600 mt-2">
                {outlet.name} - {outlet.location}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/settings')}
              className="px-6 py-3 rounded-2xl bg-gray-200 text-gray-800 shadow hover:bg-gray-300 transition-all font-semibold"
            >
              Settings
            </button>
            <button
              type="button"
              onClick={() => setShowLogoutModal(true)}
              className="px-6 py-3 rounded-2xl bg-red-500 text-white shadow-lg hover:bg-red-600 hover:shadow-xl transition-all font-semibold"
            >
              Logout
            </button>
          </div>
        </div>
        
        {/* Scanning State */}
        {state === 'scanning' && (
          <div className="bg-white rounded-3xl shadow-xl p-12">
            <div className="text-center mb-8">
              <div className="text-6xl mb-4">📱</div>
              <h2 className="text-3xl font-bold mb-2">Scan Transaction QR Code</h2>
              <p className="text-gray-600">
                Point the camera at the QR code from your payment receipt
              </p>
            </div>
            
            <div className="max-w-md mx-auto">
              <QRScanner onScan={handleQRScan} />
            </div>

            <div className="max-w-md mx-auto mt-8 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500 mb-3 text-center">
                QR code not working? Enter order ID manually
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  autoFocus
                  value={manualOrderId}
                  onChange={(e) => setManualOrderId(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && manualOrderId.trim()) {
                      e.preventDefault();
                      handleQRScan(manualOrderId.trim());
                    }
                  }}
                  placeholder="e.g. PB-20260209-5E1C60CA"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => {
                    const id = manualOrderId.trim();
                    if (id) handleQRScan(id);
                  }}
                  disabled={!manualOrderId.trim()}
                  className="px-6 py-3 rounded-xl bg-cyan-500 text-white font-semibold hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Validating State */}
        {state === 'validating' && (
          <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
            <div className="text-6xl mb-4 animate-pulse">🔍</div>
            <h2 className="text-3xl font-bold mb-2">Validating Transaction...</h2>
            <p className="text-gray-600">Please wait</p>
          </div>
        )}
        
        {/* Capturing + Reviewing: unified full-width layout so camera stays mounted during review */}
        {isSessionActive && session && (() => {
          const effective = resolveCaptureConfig(session.product, captureConfig);
          const isReviewing = state === 'reviewing';
          const showStorageError = isReviewing && storageError && lastCapturedBlob;

          if (showStorageError) {
            return (
              <div className="bg-white rounded-3xl shadow-xl p-12 max-w-4xl mx-auto">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold mb-2">Photo Session</h2>
                  <p className="text-gray-600">Customer: {session.transaction.customerName}</p>
                  <p className="text-gray-500 text-sm mt-1">{session.product.name}</p>
                </div>
                {storageErrorPreviewUrl && (
                  <div className="relative aspect-[3/4] bg-gray-900 rounded-2xl overflow-hidden shadow-2xl max-w-2xl mx-auto">
                    <img
                      src={storageErrorPreviewUrl}
                      alt="Captured"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl text-center">
                  <p className="text-red-700 font-semibold mb-2">Could not save photo</p>
                  <p className="text-red-600 text-sm mb-4">{storageError}</p>
                  <button
                    type="button"
                    onClick={handleRetakeAfterStorageError}
                    className="px-6 py-3 rounded-xl bg-cyan-500 text-white font-semibold hover:bg-cyan-600"
                  >
                    Retake
                  </button>
                </div>
              </div>
            );
          }

          return (
            <div className="bg-white rounded-3xl shadow-xl p-4 md:p-8">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <h2 className="text-2xl md:text-3xl font-bold">Photo Session</h2>
                <div className="text-right">
                  <p className="text-gray-600">Customer: {session.transaction.customerName}</p>
                  <p className="text-gray-500 text-sm mt-0.5">{session.product.name}</p>
                </div>
              </div>
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,2.5fr)_minmax(0,0.8fr)] gap-4 md:gap-6 items-start">
                {/* Left: thumbnails in 3 columns */}
                <div className="flex flex-col gap-2">
                  <p className="text-sm font-medium text-gray-600">Taken</p>
                  <div className="grid grid-cols-3 gap-2">
                    {session.capturedPreviewUrls.map((url, i) => (
                      <div
                        key={url}
                        className="aspect-[3/4] rounded-2xl overflow-hidden bg-gray-900 shadow-lg min-w-0"
                      >
                        <img
                          src={url}
                          alt={`Shot ${i + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                    {Array.from({ length: Math.max(0, session.totalShots - session.capturedPreviewUrls.length) }).map((_, i) => (
                      <div
                        key={`empty-${i}`}
                        className="aspect-[3/4] rounded-2xl border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-xs bg-gray-50 min-w-0"
                      >
                        {session.capturedPreviewUrls.length + i + 1}
                      </div>
                    ))}
                  </div>
                </div>
                {/* Center: one CameraPreview (hidden during review so stream stays alive) + PhotoReview overlay when reviewing; top-aligned with Taken thumbnails */}
                <div className={`relative flex flex-col items-start justify-start self-stretch min-h-[320px] md:min-h-[400px] h-[calc(100vh-6rem)] max-h-[calc(100vh-6rem)] ${isReviewing ? 'min-h-[50vh]' : ''}`}>
                  <div className="w-full max-w-2xl h-full aspect-[3/4] relative">
                    <div className={isReviewing ? 'relative inset-0 min-h-[280px] invisible' : ''}>
                      <CameraPreview
                        onCapture={handlePhotoCapture}
                        shotNumber={session.capturedPhotos + 1}
                        totalShots={session.totalShots}
                        countdownSeconds={effective.countdownSeconds}
                        autoStartNextShot={session.capturedPhotos > 0 && session.capturedPhotos < session.totalShots}
                        onCountdownChange={setCountdownValue}
                        hidden={isReviewing}
                        onStartClick={handleCaptureStartClick}
                      />
                    </div>
                    {isReviewing && lastCapturedBlob && (
                      <div className="absolute inset-0 z-10 flex items-start justify-center">
                        <PhotoReview
                          key={session.capturedPhotos}
                          imageBlob={lastCapturedBlob}
                          durationSeconds={effective.reviewPhotoSeconds}
                          shotNumber={session.capturedPhotos + 1}
                          totalShots={session.totalShots}
                          onComplete={handleReviewComplete}
                          onCountdownChange={setReviewCountdown}
                        />
                      </div>
                    )}
                  </div>
                </div>
                {/* Right: countdown and shot progress (outside canvas) */}
                <div className="flex flex-col items-center gap-4">
                  {!isReviewing && countdownValue !== null && (
                    <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl bg-gray-900 flex flex-col items-center justify-center shadow-xl">
                      <span className="text-5xl md:text-7xl font-bold text-white animate-pulse">
                        {countdownValue}
                      </span>
                    </div>
                  )}
                  {isReviewing && reviewCountdown !== null && (
                    <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl bg-gray-900 flex flex-col items-center justify-center shadow-xl px-2">
                      <span className="text-sm md:text-base text-white/90 mb-1">Next shot in</span>
                      <span className="text-5xl md:text-7xl font-bold text-white animate-pulse">
                        {reviewCountdown}
                      </span>
                    </div>
                  )}
                  <div className="px-4 py-2 bg-cyan-50 rounded-full">
                    <span className="text-cyan-700 font-semibold">
                      Shot {session.capturedPhotos + 1} / {session.totalShots}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
        
        {/* Completed State */}
        {state === 'completed' && session && (
          <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
            <div className="text-8xl mb-6">✅</div>
            <h2 className="text-3xl font-bold mb-4">Session Complete!</h2>
            <p className="text-gray-600 text-lg mb-8">
              All {session.totalShots} photos have been captured successfully.
            </p>
            <p className="text-gray-600 mb-8">
              Please proceed to Edit to customize your photos and print.
            </p>
            <p className="text-gray-500 text-sm mb-6">
              Returning to scan in {returnCountdown ?? 5} seconds...
            </p>
            <button
              onClick={handleStartNew}
              className="px-8 py-4 rounded-2xl bg-gradient-to-r from-cyan-400 to-blue-500 text-white text-lg font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105"
            >
              Start New Session
            </button>
          </div>
        )}
        
        {/* Error State */}
        {state === 'error' && (
          <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
            <div className="text-8xl mb-6">⚠️</div>
            <h2 className="text-3xl font-bold mb-4 text-red-600">Error</h2>
            <p className="text-gray-700 text-lg mb-8">{error}</p>
            <p className="text-gray-500 text-sm">
              Returning to scanner in {errorReturnCountdown ?? 5} seconds...
            </p>
          </div>
        )}
      </div>

      {showLogoutModal && authState?.outletId && (
        <LogoutModal
          outletId={authState.outletId}
          terminalName="Capture"
          pinRole="capture"
          onClose={() => setShowLogoutModal(false)}
          onLogout={async () => {
            await clearAuthentication();
            navigate('/auth');
          }}
        />
      )}
    </div>
  );
}
